import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';
import util from 'preact-util';
import { MapContainer, TileLayer, GeoJSON } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

import { fileBaseName, gpxDistanceKm, gpxAscentM } from './fileList';

// «Vis på kart» bottom drawer for a GPX file in the race / race class «Filer»
// list (2026-10-02). Detail APIs ship files as metadata only, so the track is
// fetched here from GET /api/races/public/:id/files/:md5/geojson (or the
// /api/raceclasses/public/... twin when raceClassId is set) and drawn with the
// same react-leaflet + Kartverket stack as components/map.
//
// Props: raceId or raceClassId, file ({ name, md5, ext, gpxInfo }), drawerLevel.

const PILL = 'badge rounded-pill me-1';
const PILL_STYLE = 'font-size: 0.75rem; font-weight: 500;';
const TRACK_STYLE = { color: '#e3007a', weight: 4, opacity: 0.9 };

export function trackBounds(geoJSON) {
    try {
        const bounds = L.geoJSON(geoJSON).getBounds();
        return bounds && bounds.isValid() ? bounds : null;
    } catch (e) {
        return null;
    }
}

@observer
class RaceFileMapDrawer extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            error: false,
            track: null,
        };
    }

    componentDidMount() {
        this.loadTrack();
    }

    loadTrack = async () => {
        const { raceId, raceClassId, file = {} } = this.props;
        const base = raceClassId > 0
            ? `/api/raceclasses/public/${raceClassId}`
            : (raceId > 0 ? `/api/races/public/${raceId}` : '');
        if (!base || !file.md5) {
            this.setState({ loading: false, error: true });
            return;
        }
        const response = await util.fetchApi(`${base}/files/${file.md5}/geojson`, { method: 'GET' }, {});
        const track = response?.status === 200 ? response.data : null;
        this.setState({ loading: false, error: !track || !track.geoJSON, track });
    }

    render() {
        const { file = {} } = this.props;
        const { loading, error, track } = this.state;
        // Prefer the stats that came with the track; fall back to the list's metadata.
        const stats = track && track.gpxInfo ? { gpxInfo: track.gpxInfo } : file;
        const km = gpxDistanceKm(stats);
        const ascent = gpxAscentM(stats);
        const bounds = track ? trackBounds(track.geoJSON) : null;
        return (
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 40px;'>
                    <div class='font-weight-lighter px-3 box-header mt-0 d-flex flex-row justify-content-between align-items-center'>
                        <span><i class='fa-duotone fa-route me-2' />{fileBaseName(file)}</span>
                    </div>
                    <div class='px-3 mb-2'>
                        {km > 0 && (
                            <span class={`${PILL} text-bg-light`} style={PILL_STYLE} title='Distanse'>
                                <i class='fa-duotone fa-route me-1' />{km >= 10 ? Math.round(km) : `${Math.round(km * 10) / 10}`.replace('.', ',')} km
                            </span>
                        )}
                        {ascent > 0 && (
                            <span class={`${PILL} text-bg-light`} style={PILL_STYLE} title='Stigning'>
                                <i class='fa-duotone fa-arrow-trend-up me-1' />{`${ascent}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} m
                            </span>
                        )}
                    </div>
                    <div class='mx-3' style='height: calc(100vh - 230px); min-height: 320px; border-radius: 10px; overflow: hidden;'>
                        {loading && (
                            <div class='h-100 d-flex justify-content-center align-items-center text-body-secondary'>
                                <span class='spinner-border spinner-border-sm me-2' role='status' />
                                <Text id='race.track-loading'>Loading track...</Text>
                            </div>
                        )}
                        {!loading && (error || !bounds) && (
                            <div class='h-100 d-flex justify-content-center align-items-center text-body-secondary'>
                                <Text id='race.track-load-failed'>Could not load the track.</Text>
                            </div>
                        )}
                        {!loading && !error && bounds && (
                            <MapContainer
                                bounds={bounds}
                                boundsOptions={{ padding: [20, 20] }}
                                scrollWheelZoom={true}
                                style={{ height: '100%', width: '100%' }}
                            >
                                <TileLayer
                                    attribution='&copy; <a href="https://statkart.no">Kartverket</a>'
                                    url='https://cache.kartverket.no/v1/wmts/1.0.0/topograatone/default/webmercator/{z}/{y}/{x}.png'
                                />
                                <GeoJSON key={track.md5} data={track.geoJSON} style={() => TRACK_STYLE} />
                            </MapContainer>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

export default RaceFileMapDrawer;
